import parse from "html-react-parser"
import { addDisplayFormatter } from "aldoo-ra/CMS/field-display-format-resolver"
import StorePriceFormatter from "aldoo-ra/CMSModels/StorePrice/formatters/store-price-formatter"
import DatabaseModelTypeResolver from "aldoo-ra/CMS/database-model-type-resolver"
import { twJoin } from "tailwind-merge"
import { formatMoney } from "aldoo-ra/Store/currency"

const { postFormat, preFormat } = StorePriceFormatter

/**
 * Format a given price and currency
 * Currency objects are also supported
 * @param {*} price
 * @param {*} currency
 * @returns
 */
export const priceDisplayFormat = (price, currency) => {
  //invalid input
  if (price == undefined && !currency) return ""

  //if the price is a currency object
  if (price?.amount !== undefined && price?.currency !== undefined) {
    const original = { ...price }
    price = original.amount
    currency = original.currency
  }

  //invalid input
  if (price == undefined || !currency) return ""

  currency = currency.toLowerCase()
  //not a money currency (coins): the amount and its name
  if (!/^[a-z]{3}$/.test(currency)) return `${price}${currency}`
  //money, as English writes it: $12.00, €12.00, BGN 12.00
  return formatMoney(parseFloat(price), currency.toUpperCase(), { locale: "en" })
}

const Badge = ({ children, className = "" }) =>
  `<div class="${twJoin(
    "badge text-[14px] px-2 py-1 rounded",
    className
  )}">${children}</div>`

const PrimaryBadge = () =>
  Badge({
    children: "Primary",
    className:
      "text-white bg-admin_primary rounded-[15px] py-1 px-3 mr-4 w-[80px] text-center badge-primary",
  })

const PaymentMethodBadge = ({ required }) =>
  required
    ? Badge({
        children: "Require Payment Method",
        className: "text-text admin_neutral_2 whitespace-nowrap",
      })
    : Badge({
        children: "No Credit Card Required",
        className: "text-text admin_neutral_2 whitespace-nowrap",
      })

const getTrialPeriod = (item) => {
  const { trial_duration, trial_duration_in_days } = item

  if (
    !trial_duration ||
    trial_duration === "no-trial" ||
    !trial_duration_in_days
  ) {
    return ""
  }

  const trialText = trial_duration === "custom"
    ? `with a ${trial_duration_in_days} days trial`
    : `with a 1 ${trial_duration} trial`

  return `<span class="whitespace-nowrap">${trialText}</span>`
}

export const getPaymentDuration = (duration, duration_in_days) => {
  if (duration === "one-time-payment" || duration === undefined) return ""

  const table = { [365]: "year", [30]: "month", [7]: "week", [1]: "day" }
  duration = table[parseInt(duration)] || duration

  return `/ ${duration !== "custom" ? duration : `${duration_in_days} days`}`
}

export const priceOverview = (item) => {
  if (!item?.currency) return "Add a price model"

  const isSubscription =
    item.duration !== undefined && item.duration !== "one-time-payment"
  const isFree = parseInt(item.amount) === 0

  if (isFree) {
    return parse(`${item.primary ? PrimaryBadge() : ""} <b>Free</b>`)
  }

  const paymentType = isSubscription
    ? "<b>Subscription</b>"
    : "One time <b>Purchase</b>"
  const paymentDuration = getPaymentDuration(
    item.duration,
    item.duration_in_days
  )
  const trial = isSubscription ? getTrialPeriod(item) : ""
  const paymentMethodBadge = isSubscription
    ? PaymentMethodBadge({ required: item.requires_payment_method })
    : ""

  const content = `
    <div class="${twJoin(
      "relative flex flex-row items-center gap-2",
      item.primary ? "border-2 border-primary p-3 rounded-lg" : ""
    )}">
      ${paymentType} for <b>${priceDisplayFormat(
    item.amount,
    item.currency
  )}</b> ${paymentDuration} ${trial}
      ${paymentMethodBadge}
      ${
        item.primary
          ? Badge({ children: "Primary", className: "text-text_inv bg-admin_neutral_3" })
          : ""
      }
    </div>
  `

  return parse(content)
}

export const priceSearchResult = (item) => {
  const { getType } = DatabaseModelTypeResolver()
  const StorePrice = getType("StorePrice")

  let data = { ...item }

  let primaryPrice = data.price_model?.find((price) => price.primary)

  if (typeof primaryPrice?.duration === "string") {
    data = postFormat(data, StorePrice)
    //set again but this time formatter
    primaryPrice = data.price_model.find((price) => price.primary)
  }

  if (!primaryPrice) {
    return parse(`<span class="font-medium text-sm">+ Add Price</span>`)
  }

  // Helper functions for formatting phases (same as price-model-editor)
  const formatInterval = (phase) => {
    const count = phase.interval_count || 1
    const interval = phase.interval || 'month'
    return count === 1 ? interval : `${count} ${interval}s`
  }

  const formatDuration = (phase) => {
    const iterations = phase.iterations
    const count = phase.interval_count || 1
    const interval = phase.interval || 'month'
    if (!iterations) return null
    const totalUnits = iterations * count
    return totalUnits === 1 ? `${totalUnits} ${interval}` : `${totalUnits} ${interval}s`
  }

  // Generate title from phases if they exist
  const phases = primaryPrice?.phases
  if (phases && Array.isArray(phases) && phases.length > 0) {
    const enabledPhases = phases.filter(p => p.enabled !== false)
    const trial = enabledPhases.find(p => p.phase_type === 'trial')
    const introductory = enabledPhases.find(p => p.phase_type === 'introductory')
    const regular = enabledPhases.find(p => p.phase_type === 'regular')

    if (regular) {
      let priceText = ''
      let trialChip = ''
      let introChip = ''

      if (regular.amount === 0) {
        priceText = '<b>Free</b>'
      } else {
        const regularPrice = priceDisplayFormat(regular.amount, regular.currency)

        // Check if this is a one-time purchase (no interval) or subscription
        if (regular.interval) {
          const regularInterval = formatInterval(regular)
          priceText = `<b>${regularPrice}/${regularInterval}</b>`
        } else {
          // One-time purchase - just show the price
          priceText = `<b>${regularPrice}</b>`
        }
      }

      if (trial && trial.amount === 0) {
        const trialDuration = formatDuration(trial)
        if (trialDuration) {
          trialChip = `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 ml-2">${trialDuration} trial</span>`
        }
      }

      if (introductory) {
        const introPrice = priceDisplayFormat(introductory.amount, introductory.currency)
        introChip = `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 ml-2">${introPrice} introductory</span>`
      }

      return parse(`<span>${priceText}${trialChip}${introChip}</span>`)
    }
  }

  // Fallback to old format if no phases
  const purchaseType = parseInt(primaryPrice.duration)
    ? "Subscription"
    : "One-time Purchase"
  const table = { [365]: "year", [30]: "month", [7]: "week", [1]: "day" }

  let period = primaryPrice.duration
  period = table[parseInt(period)] || (period ? `${period} days` : "")
  period = period ? `/ ${period}` : ""
  const timing = primaryPrice.auto_renew ? "Auto-Renew" : ""
  return parse(`<span><b>${priceDisplayFormat(
    primaryPrice
  )} ${period}</b> - ${purchaseType} ${timing}</span>`)
}

// Note: CMS registration is now handled in the StorePrice model index.js
